import { useState } from "react";
import { useCreate, useNotify, useRefresh } from "ra-core";
import { Plus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";

import type { ChangelogType, ChangelogAudience } from "./ChangelogEntry";

// Sprint 9.6 — AddManualSheet : insertion manuelle d'une entrée changelog.
// Workflow hybride D : pré-remplissage par cron IA → édition/validation
// par admin. Pour l'instant : 100% manuel (cron sera ajouté Sprint 9.6b).

const TYPE_OPTIONS: { value: ChangelogType; label: string }[] = [
  { value: "feature", label: "Nouveauté" },
  { value: "improvement", label: "Amélioration" },
  { value: "fix", label: "Correctif" },
  { value: "breaking", label: "Breaking" },
];

const AUDIENCE_OPTIONS: { value: ChangelogAudience; label: string }[] = [
  { value: "internal", label: "Interne" },
  { value: "external", label: "Public" },
];

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function ChangelogAddManualSheet() {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<ChangelogType>("feature");
  const [audience, setAudience] = useState<ChangelogAudience>("internal");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [releasedAt, setReleasedAt] = useState(todayIso());

  const [create, { isPending }] = useCreate();
  const notify = useNotify();
  const refresh = useRefresh();

  const reset = () => {
    setType("feature");
    setAudience("internal");
    setTitle("");
    setDescription("");
    setReleasedAt(todayIso());
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !description.trim()) return;
    create(
      "changelog_entries",
      {
        data: {
          type,
          audience,
          title: title.trim(),
          description: description.trim(),
          released_at: releasedAt,
        },
      },
      {
        onSuccess: () => {
          notify("Entrée ajoutée au changelog", { type: "success" });
          reset();
          setOpen(false);
          refresh();
        },
        onError: (err: unknown) => {
          notify(`Échec : ${err instanceof Error ? err.message : "erreur"}`, {
            type: "error",
          });
        },
      },
    );
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button type="button" size="sm" className="gap-1.5 h-8">
          <Plus className="size-3.5" />
          Ajouter
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Ajouter une entrée changelog</SheetTitle>
          <SheetDescription>
            Saisie manuelle d'une release note. À utiliser pour valider /
            compléter le pré-remplissage IA hebdo.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4 px-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="type" className="text-[12px]">
                Type
              </Label>
              <select
                id="type"
                value={type}
                onChange={(e) => setType(e.target.value as ChangelogType)}
                className="mt-1 w-full h-9 rounded-md border border-border bg-background px-2 text-sm"
              >
                {TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="audience" className="text-[12px]">
                Audience
              </Label>
              <select
                id="audience"
                value={audience}
                onChange={(e) =>
                  setAudience(e.target.value as ChangelogAudience)
                }
                className="mt-1 w-full h-9 rounded-md border border-border bg-background px-2 text-sm"
              >
                {AUDIENCE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <Label htmlFor="released_at" className="text-[12px]">
              Date de release
            </Label>
            <Input
              id="released_at"
              type="date"
              value={releasedAt}
              onChange={(e) => setReleasedAt(e.target.value)}
              className="mt-1"
              required
            />
          </div>

          <div>
            <Label htmlFor="title" className="text-[12px]">
              Titre
            </Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex : Refonte fiche contact"
              className="mt-1"
              required
            />
          </div>

          <div>
            <Label htmlFor="description" className="text-[12px]">
              Description
            </Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Détaille les changements visibles pour les utilisateurs…"
              rows={5}
              className="mt-1"
              required
            />
          </div>

          <SheetFooter className="gap-2">
            <SheetClose asChild>
              <Button type="button" variant="outline">
                Annuler
              </Button>
            </SheetClose>
            <Button type="submit" disabled={isPending}>
              {isPending ? "Ajout…" : "Ajouter"}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
